import * as React from "react";
import {useCallback, useState} from "react";
import {Rental, RentalTypes} from "./types";
import {
    classNames,
    CloseButton,
    Dialog,
    DialogFooter,
    DialogHeader,
    layout,
    PushButton,
    theme,
    typography,
    useOccupancies,
    usePlatform,
    View,
    ViewContent,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import {useItems} from "./hooks";
import {DateTime} from "luxon";
import {stylesheet} from "typestyle";
import {BoxControl} from "./admin/BoxControl";
import {RentalStateDisplay} from "./RentalStateDisplay";
import {useLocalization} from "../i18n";

interface RentalLenderViewProps {
    rental: Rental;
    rentalTypes: RentalTypes;
    onClose: () => void;
}

export function RentalLenderView({rental, rentalTypes, onClose}: RentalLenderViewProps) {
    const {t, language} = useLocalization();
    const platform = usePlatform();
    const occupancies = useOccupancies();
    const items = useItems();
    const occupancy = occupancies.find(occupancy => occupancy.content.item == rental.itemUuid);
    const item = items.find(item => item.uuid == rental.itemUuid);
    const serviceItem = rental.serviceItem;

    const [pickedUpDialog, setPickedUpDialog] = useState<boolean>(false);
    const [returnedDialog, setReturnedDialog] = useState<boolean>(false);

    const handleBoxOpened = useCallback(() => {
        if(rental.lender) {
            if(!rental.lender.pickedUp) {
                setPickedUpDialog(true);
                return;
            }
            if(rental.lender.pickedUp && !rental.lender.returned) {
                setReturnedDialog(true);
                return;
            }
        }
    }, [rental, item]);

    const handleLenderPickedUp = useCallback(async () => {
        if(occupancy) {
            await platform.updateOccupancy(occupancy.uuid, {
                item: rental.itemUuid,
                rental: rental.uuid,
                lenderPickedUp: DateTime.local().toISO()
            });
            setPickedUpDialog(false);
        }
    }, [pickedUpDialog]);

    const handleLenderReturned = useCallback(async () => {
        if(occupancy) {
            await platform.updateOccupancy(occupancy.uuid, {
                item: rental.itemUuid,
                rental: rental.uuid,
                lenderPickedUp: occupancy.content.lenderPickedUp,
                lenderReturned: DateTime.local().toISO()
            });
            setReturnedDialog(false);
        }
    }, [pickedUpDialog]);

    return (
        <View>
            <ViewHeader className={theme.lessContrast}>
                <ViewHeaderItem fill className={layout.paddingMedium}>
                    <div className={typography.title}>{rentalTypes == RentalTypes.Loan ? t("Loan") : t("Rental")}</div>
                </ViewHeaderItem>
                <CloseButton label={t("Close")} onClick={onClose}/>
            </ViewHeader>
            <ViewContent className={classNames(layout.horizontal, layout.paddingMedium, layout.alignCenter, layout.spaceEvenly)}>
                <div className={classNames(layout.horizontal)}>
                    <div className={classNames(theme.moreContrast, layout.vertical, styles.boxLeft)}>
                        <div className={typography.bigger}>
                            {rental.state &&
                            <div className={classNames(layout.marginYLarge, layout.alignCenter)}>
                                <RentalStateDisplay rentalState={rental.state} />
                            </div>
                            }

                            <div className={layout.marginYLarge}>
                                <div className={typography.caption}>{t("Borrower")}</div>
                                <div>{rental.borrower.name}</div>
                            </div>

                            {serviceItem &&
                            <div className={layout.marginYLarge}>
                                <div className={typography.caption}>{t("ServiceItem")}</div>
                                <div className={layout.marginYSmall}>
                                    <div>{serviceItem.name}</div>
                                </div>
                            </div>
                            }

                            <div className={layout.marginYLarge}>
                                <div className={typography.caption}>{t("Timeframe")}</div>
                                <div className={layout.marginYSmall}>
                                    <div>
                                        {DateTime.fromISO(rental.from).setLocale(language).toLocaleString(DateTime.DATETIME_SHORT)}
                                    </div>
                                    <div>{t("TimeframeTo")}</div>
                                    <div>
                                        {DateTime.fromISO(rental.until).setLocale(language).toLocaleString(DateTime.DATETIME_SHORT)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={classNames(theme.lessContrast, layout.vertical, layout.spaceBetween, styles.boxRight)}>
                        {(rental.lender && occupancy) &&
                        <div className={styles.footerAction}>
                            <BoxControl boxNumber={occupancy.boxNumber} onBoxOpened={handleBoxOpened} />
                        </div>
                        }
                    </div>
                </div>
            </ViewContent>
            {serviceItem &&
            <React.Fragment>
                <Dialog open={pickedUpDialog} onBackdropClick={() => setPickedUpDialog(false)}>
                    <DialogHeader title={t("ServiceItemPickup")} />
                    <div className={layout.paddingMedium}>
                        {t("ServiceItemPickupInfo", { serviceItem: serviceItem.name })}
                    </div>
                    <DialogFooter>
                        <div className={layout.paddingMedium}>
                            <PushButton success onClick={handleLenderPickedUp}>{t("Confirm")}</PushButton>
                        </div>
                        <div className={layout.paddingMedium}>
                            <PushButton danger onClick={() => setPickedUpDialog(false)}>{t("Cancel")}</PushButton>
                        </div>
                    </DialogFooter>
                </Dialog>
                <Dialog open={returnedDialog} onBackdropClick={() => setReturnedDialog(false)}>
                    <DialogHeader title={t("ServiceItemReturn")} />
                    <div className={layout.paddingMedium}>
                        {t("ServiceItemReturnInfo", { serviceItem: serviceItem.name })}
                    </div>
                    <DialogFooter>
                        <div className={layout.paddingMedium}>
                            <PushButton success onClick={handleLenderReturned}>{t("Confirm")}</PushButton>
                        </div>
                        <div className={layout.paddingMedium}>
                            <PushButton danger onClick={() => setReturnedDialog(false)}>{t("Cancel")}</PushButton>
                        </div>
                    </DialogFooter>
                </Dialog>
            </React.Fragment>
            }
        </View>
    );
}

const styles = stylesheet({
    boxLeft: {
        width: "24em",
        padding: "1.5em"
    },
    boxRight: {
        width: "20em",
        padding: "1.5em"
    },
    button: {
        width: "100%"
    },
    footerAction: {
        paddingTop: "1.5em"
    },
});
